import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { brl, fmtTime, fmtDate } from "@/lib/format";
import { CalendarDays, DollarSign, Scissors, TrendingUp, Clock, Share2, Copy, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useMe } from "@/lib/permissions";
import { formatAppointmentServices } from "@/lib/appointment";

const StatCard = ({ label, value, icon: Icon, tid }) => (
  <div data-testid={tid} className="surface p-6 flex flex-col gap-3 transition-transform duration-300 hover:-translate-y-0.5">
    <div className="flex items-center justify-between">
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
        <Icon className="h-4 w-4" />
      </div>
    </div>
    <div className="text-2xl sm:text-3xl font-display font-bold tracking-tight">{value}</div>
  </div>
);

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [today, setToday] = useState([]);
  const [loading, setLoading] = useState(true);
  const me = useMe();
  const isProLogin = !!me?.professional_id;
  const canSeeCommissions = !isProLogin || (me?.permissions || []).includes("reports.view_commission");
  const bookingLink = `${window.location.origin}/agendar`;

  const copyLink = () => { navigator.clipboard.writeText(bookingLink); toast.success("Link copiado!"); };
  const shareWhats = () => {
    const msg = encodeURIComponent(`Agende seu horário online: ${bookingLink}`);
    window.open(`https://wa.me/?text=${msg}`, "_blank");
  };

  const load = async () => {
    const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
    const endOfToday = new Date(); endOfToday.setHours(23, 59, 59, 999);
    const start30 = new Date(); start30.setDate(start30.getDate() - 30);
    try {
      const [{ data: s }, { data: a }] = await Promise.all([
        api.get("/reports/summary", { params: { start: start30.toISOString(), end: new Date().toISOString() } }),
        api.get("/appointments", { params: { start: startOfToday.toISOString(), end: endOfToday.toISOString() } }),
      ]);
      setSummary(s); setToday(a);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-8 animate-fade-in-up">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">Painel</h1>
          <p className="text-sm text-muted-foreground mt-1">Visão geral dos últimos 30 dias.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-sm text-muted-foreground hidden sm:block">{fmtDate(new Date())}</div>
          <Dialog>
            <DialogTrigger asChild>
              <Button data-testid="btn-share-link" className="h-11"><Share2 className="h-4 w-4 mr-2" /> Compartilhar agenda</Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>Link público da agenda</DialogTitle></DialogHeader>
              <p className="text-sm text-muted-foreground">Compartilhe este link com seus clientes. Eles poderão criar conta, ver horários livres e agendar sozinhos — o agendamento aparecerá aqui na sua agenda automaticamente.</p>
              <div className="flex items-center gap-2">
                <Input data-testid="input-booking-link" readOnly value={bookingLink} className="h-11" />
                <Button data-testid="btn-copy-link" variant="outline" size="icon" onClick={copyLink}><Copy className="h-4 w-4" /></Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button data-testid="btn-share-whatsapp" variant="secondary" onClick={shareWhats}><MessageCircle className="h-4 w-4 mr-2" /> WhatsApp</Button>
                <Button variant="outline" onClick={() => window.open(bookingLink, "_blank")}>Abrir página</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {canSeeCommissions && <StatCard label="Faturamento" value={brl(summary?.total_revenue)} icon={DollarSign} tid="stat-revenue" />}
        {canSeeCommissions && <StatCard label="Receita Líquida" value={brl(summary?.net_revenue)} icon={TrendingUp} tid="stat-net" />}
        {canSeeCommissions && <StatCard label="Comissões" value={brl(summary?.total_commissions)} icon={Scissors} tid="stat-commissions" />}
        <StatCard label="Atendimentos" value={summary?.completed_appointments || 0} icon={CalendarDays} tid="stat-completed" />
      </div>

      <div className="surface p-6">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="h-4 w-4 text-primary" />
          <h2 className="font-display font-semibold text-lg">Agenda de hoje</h2>
          <span className="ml-auto text-sm text-muted-foreground">{today.length} agendamento(s)</span>
        </div>
        {loading ? (
          <div className="text-sm text-muted-foreground py-8 text-center">Carregando...</div>
        ) : today.length === 0 ? (
          <div className="text-sm text-muted-foreground py-8 text-center">Nenhum agendamento para hoje.</div>
        ) : (
          <div className="divide-y divide-border -mx-6">
            {today.map((a) => (
              <div key={a.id} data-testid={`today-appt-${a.id}`} className="flex items-center gap-4 px-6 py-3 hover:bg-muted/40 transition-colors">
                <div className="w-16 text-sm font-medium">{fmtTime(a.scheduled_at)}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{a.client_name}</div>
                  <div className="text-xs text-foreground/90 break-words"><span className="font-medium">{formatAppointmentServices(a)}</span></div>
                  <div className="text-xs text-muted-foreground truncate">{a.professional_name}</div>
                </div>
                <div className="text-sm font-medium">{brl(a.price)}</div>
                <span className={`text-xs px-2 py-1 rounded-md border ${a.status === "completed" ? "border-primary/30 text-primary bg-primary/5" : a.status === "canceled" ? "border-destructive/30 text-destructive bg-destructive/5" : "border-border text-muted-foreground"}`}>
                  {a.status === "scheduled" ? "Agendado" : a.status === "completed" ? "Concluído" : a.status === "canceled" ? "Cancelado" : "Faltou"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
